import './SourceUsageByModelChart.scss';
import React, { useMemo, useState } from 'react';
import SimpleBar from 'simplebar-react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from 'primereact/skeleton';
import EmptyPanel from '../../../../../shared/components/empty-panel/EmptyPanel';
import { PlatformStatsForDomainResponse } from '../../../../../shared/services/monitor/data/sources/MonitorDataSourcesService';
import { canShowModel, getTrendValue } from '../../../../../shared/utils/utils';
import { getModelNameIcon, getModelNameLabel } from '../../../../../shared/helpers/ChatTypeHelpers';
import { MODEL_KEY_TO_CHAT_TYPES } from '../../../../../shared/helpers/MonitorTypeHelpers';
import { TrendTag } from '../../../../projects/preview/components/trend-tag/TrendTag';
import { ChatType } from '../../../../../shared/types/monitor/ChatType';
import useScrollFade from '../../../../../shared/hooks/scroll/UseScrollFade';

interface ModelRow {
  platform: string;
  icon: string | null;
  label: string;
  share: number;
  count: number;
  countChange: number | undefined;
}

interface SourceUsageByModelChartProps {
  currentPlatformStats: PlatformStatsForDomainResponse | null;
  previousPlatformStats: PlatformStatsForDomainResponse | null;
  isLoading: boolean;
  features: string[];
  chatType: ChatType | null;
}

type PeriodView = 'current' | 'previous';

const ALL_MODEL_KEYS = MODEL_KEY_TO_CHAT_TYPES.map((m) => m.key);

const PERIOD_COLORS = {
  current: '#164fce',
  previous: '#6794fa',
};

const SourceUsageByModelChart: React.FC<SourceUsageByModelChartProps> = ({
  currentPlatformStats,
  previousPlatformStats,
  isLoading,
  features,
  chatType,
}) => {
  const { t } = useTranslation();
  const [periodView, setPeriodView] = useState<PeriodView>('current');
  const { scrollRef, isAtTop, isAtBottom, handleScroll } = useScrollFade();

  const hasPreviousData = (previousPlatformStats?.platforms?.length ?? 0) > 0;

  const activeStats = periodView === 'current' ? currentPlatformStats : previousPlatformStats;
  const comparisonStats = periodView === 'current' ? previousPlatformStats : currentPlatformStats;

  // When a specific chat type filter is active, only show models that match it
  const visibleModelKeys = useMemo(() => {
    if (!chatType || chatType === 'ALL') {
      return ALL_MODEL_KEYS.filter((key) => canShowModel(key, features));
    }
    // Find which model key owns this chatType
    const matchingEntry = MODEL_KEY_TO_CHAT_TYPES.find((m) =>
      m.chatTypes.includes(chatType as any)
    );
    if (matchingEntry && canShowModel(matchingEntry.key, features)) {
      return [matchingEntry.key];
    }
    return [];
  }, [chatType, features]);

  const rows: ModelRow[] = useMemo(() => {
    if (visibleModelKeys.length === 0) return [];

    const activeMap = new Map(
      (activeStats?.platforms ?? []).map((p) => [p.platform, p])
    );
    const comparisonMap = new Map(
      (comparisonStats?.platforms ?? []).map((p) => [p.platform, p.count])
    );

    return visibleModelKeys.map((key) => {
      const stat = activeMap.get(key);
      const count = stat?.count ?? 0;
      const share = stat?.share ?? 0;

      return {
        platform: key,
        icon: getModelNameIcon(key),
        label: getModelNameLabel(key),
        share,
        count,
        countChange: comparisonStats
          ? count - (comparisonMap.get(key) ?? 0)
          : undefined,
      };
    }).sort((a, b) => b.count - a.count);
  }, [activeStats, comparisonStats, visibleModelKeys]);


  return (
    <div className="stats__panel source-usage-by-model">
      <div className="page-box__title-action">
        <div className="page-box__title">{t('sourceUsageByModelChart')}</div>
      </div>
      <div className="stats-box stats-box--line-chart">
        {isLoading ? (
          <div className="stats-box__content stats-box--chart">
            <Skeleton className="skeleton--chart">
              <span />
            </Skeleton>
          </div>
        ) : rows.length === 0 ? (
          <div className="stats-box__content stats-box__content--empty">
            <EmptyPanel />
          </div>
        ) : (
          <div className={`stats-box__content source-usage-by-model__content${isAtTop ? '' : ' source-usage-by-model__content--fade-top'}${isAtBottom ? '' : ' source-usage-by-model__content--fade-bottom'}`}>
            <SimpleBar className="source-usage-by-model__scroll" autoHide={false} scrollableNodeProps={{ ref: scrollRef, onScroll: handleScroll }}>
              <div className="source-usage-by-model__list">
                {rows.map((row) => (
                  <div key={row.platform} className="source-usage-by-model__row">
                    <div className="source-usage-by-model__info">
                      <div className="source-usage-by-model__model">
                        {row.icon && (
                          <span className={`source-usage-by-model__icon ico--sm ${row.icon}`} />
                        )}
                        <span className="source-usage-by-model__label">{row.label}</span>
                        <span className="source-usage-by-model__share">{row.share.toFixed(1)}%</span>
                      </div>
                      <div className="source-usage-by-model__stats">
                        <span className="source-usage-by-model__count">{row.count}</span>
                        {hasPreviousData && row.countChange !== undefined && (
                          <TrendTag trendValue={getTrendValue(row.countChange, 0)} suffix="" />
                        )}
                      </div>
                    </div>
                    <div className="source-usage-by-model__bar-wrapper">
                      <div
                        className="source-usage-by-model__bar"
                        style={{ width: `${row.share}%`, backgroundColor: PERIOD_COLORS[periodView] }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </SimpleBar>
            {hasPreviousData && (
              <div className="source-usage-by-model__legend">
                <button
                  className={`source-usage-by-model__legend-item ${periodView === 'current' ? 'source-usage-by-model__legend-item--active' : 'source-usage-by-model__legend-item--inactive'}`}
                  onClick={() => setPeriodView('current')}
                >
                  <span className="source-usage-by-model__legend-dot source-usage-by-model__legend-dot--current" />
                  {t('currentPeriod')}
                </button>
                <button
                  className={`source-usage-by-model__legend-item ${periodView === 'previous' ? 'source-usage-by-model__legend-item--active' : 'source-usage-by-model__legend-item--inactive'}`}
                  onClick={() => setPeriodView('previous')}
                >
                  <span className="source-usage-by-model__legend-dot source-usage-by-model__legend-dot--previous" />
                  {t('previousPeriod')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SourceUsageByModelChart;
